/**
 * Override Inspection Modal
 *
 * Modal for admin to override inspection result
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BaseActionModal, { IActionModalProps, IModalChildProps } from './BaseActionModal';
import { ArbitrationAction } from '../../../services/arbitrationService';

interface OverrideInspectionModalProps extends Omit<IActionModalProps, 'action'> {
  currentInspectionStatus?: string;
}

const OverrideInspectionContent: React.FC<IModalChildProps & { currentInspectionStatus?: string }> = ({
  setPayload,
  setIsValid,
  currentInspectionStatus
}) => {
  const [overrideResult, setOverrideResult] = useState<'approved' | 'rejected' | ''>('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    setPayload({
      overrideResult,
      reason
    });
    setIsValid(overrideResult !== '' && reason.length >= 5);
  }, [overrideResult, reason, setPayload, setIsValid]);

  return (
    <Box>
      <Alert severity="warning" icon={<SearchIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          با تغییر نتیجه بازرسی، نتیجه قبلی بازرس لغو شده و نتیجه جدید اعمال می‌شود.
        </Typography>
      </Alert>

      {currentInspectionStatus && (
        <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            وضعیت فعلی بازرسی: <strong>{currentInspectionStatus}</strong>
          </Typography>
        </Box>
      )}

      <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          نتیجه جدید بازرسی:
        </Typography>
        <RadioGroup
          value={overrideResult}
          onChange={(e) => setOverrideResult(e.target.value as 'approved' | 'rejected')}
        >
          <FormControlLabel
            value="approved"
            control={<Radio color="success" />}
            label={
              <Box>
                <Typography variant="body2" fontWeight="medium">تأیید محصول</Typography>
                <Typography variant="caption" color="text.secondary">
                  محصول سالم و مطابق توضیحات است
                </Typography>
              </Box>
            }
            sx={{
              mb: 1, p: 1, borderRadius: 1,
              bgcolor: overrideResult === 'approved' ? 'success.50' : 'transparent',
              border: '1px solid',
              borderColor: overrideResult === 'approved' ? 'success.200' : 'transparent'
            }}
          />
          <FormControlLabel
            value="rejected"
            control={<Radio color="error" />}
            label={
              <Box>
                <Typography variant="body2" fontWeight="medium">رد محصول</Typography>
                <Typography variant="caption" color="text.secondary">
                  محصول مشکل‌دار یا نامطابق با توضیحات است
                </Typography>
              </Box>
            }
            sx={{
              p: 1, borderRadius: 1,
              bgcolor: overrideResult === 'rejected' ? 'error.50' : 'transparent',
              border: '1px solid',
              borderColor: overrideResult === 'rejected' ? 'error.200' : 'transparent'
            }}
          />
        </RadioGroup>
      </FormControl>

      <TextField
        fullWidth
        multiline
        rows={2}
        label="دلیل تغییر نتیجه"
        placeholder="مثال: پس از بررسی مجدد مدارک، نتیجه بازرسی نادرست تشخیص داده شد"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        helperText="حداقل ۵ کاراکتر - این دلیل ثبت می‌شود"
      />
    </Box>
  );
};

const OverrideInspectionModal: React.FC<OverrideInspectionModalProps> = ({ currentInspectionStatus, ...props }) => {
  return (
    <BaseActionModal {...props} action={ArbitrationAction.OVERRIDE_INSPECTION}>
      <OverrideInspectionContent
        currentInspectionStatus={currentInspectionStatus}
        payload={{}}
        setPayload={() => {}}
        isValid={false}
        setIsValid={() => {}}
      />
    </BaseActionModal>
  );
};

export default OverrideInspectionModal;
